import { isNativeApp } from '../device'

/**
 * Interface para o token CSRF em cache
 */
interface CsrfToken {
  token: string | null
  timestamp: number
  fetching: boolean
}

/**
 * Gerenciador singleton para CSRF token
 *
 * Evita múltiplas chamadas simultâneas ao endpoint de CSRF
 * e cacheia o token por 55 minutos (o token Laravel expira em 60 minutos)
 */
class CsrfManager {
  private token: CsrfToken = {
    token: null,
    timestamp: 0,
    fetching: false,
  }

  private pendingRequests: Array<(token: string | null) => void> = []
  private readonly TOKEN_TTL = 55 * 60 * 1000 // 55 minutos

  /**
   * Obtém o token CSRF do cookie XSRF-TOKEN
   */
  private getTokenFromCookie(): string | null {
    if (typeof document === 'undefined' || !document.cookie) {
      return null
    }

    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=')
      if (name === 'XSRF-TOKEN') {
        try {
          return decodeURIComponent(value)
        } catch {
          return value
        }
      }
    }
    return null
  }

  /**
   * Busca o cookie CSRF no servidor
   */
  private async fetchCsrfCookie(): Promise<boolean> {
    if (isNativeApp()) {
      return false
    }

    const apiUrl = (import.meta as any).env?.VITE_API_BASE_URL || 'https://bloopbrasil.com.br/api'
    const sanctumBaseUrl = (import.meta as any).env?.VITE_SANCTUM_BASE_URL || apiUrl.replace(/\/api\/?$/, '')

    const csrfUrl = `${sanctumBaseUrl}/sanctum/csrf-cookie`

    try {
      // Usar fetch (funciona tanto em web quanto em apps nativos)
      await fetch(csrfUrl, {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      })

      // Verificar se o cookie foi definido
      const newToken = this.getTokenFromCookie()
      if (newToken) {
        this.token.token = newToken
        this.token.timestamp = Date.now()
        return true
      }

      return false
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[CSRF Manager] Erro ao buscar cookie:', error)
      }
      return false
    }
  }

  /**
   * Obtém o token CSRF, buscando se necessário
   *
   * @param force Force uma nova busca mesmo se tiver token em cache
   * @returns O token CSRF ou null se não disponível
   */
  async getToken(force = false): Promise<string | null> {
    // Verificar se o token em cache ainda é válido
    const now = Date.now()
    const isExpired = now - this.token.timestamp > this.TOKEN_TTL

    if (!force && this.token.token && !isExpired) {
      return this.token.token
    }

    // Se já está buscando, aguardar a busca atual
    if (this.token.fetching) {
      return new Promise((resolve) => {
        this.pendingRequests.push(resolve)
      })
    }

    // Iniciar busca
    this.token.fetching = true

    try {
      // Tentar obter do cookie primeiro (pode já ter sido definido)
      let token = this.getTokenFromCookie()

      // Se não tem token ou está expirado, buscar novo
      if (!token || force || isExpired) {
        const success = await this.fetchCsrfCookie()
        if (success) {
          token = this.getTokenFromCookie()
        }
      }

      // Atualizar cache
      this.token.token = token || null
      this.token.timestamp = now

      // Resolver todas as promises pendentes
      for (const resolve of this.pendingRequests) {
        resolve(this.token.token)
      }
      this.pendingRequests = []

      return this.token.token
    } finally {
      this.token.fetching = false
    }
  }

  /**
   * Obtém o token de forma síncrona (do cache)
   * Útil para interceptores que não podem ser async
   */
  getTokenSync(): string | null {
    const now = Date.now()
    const isExpired = now - this.token.timestamp > this.TOKEN_TTL

    if (this.token.token && !isExpired) {
      return this.token.token
    }

    // Tentar do cookie
    const token = this.getTokenFromCookie()
    if (token) {
      this.token.token = token
      this.token.timestamp = now
    }

    return token
  }

  /**
   * Invalida o token em cache (forçar nova busca na próxima requisição)
   */
  invalidate(): void {
    this.token.token = null
    this.token.timestamp = 0
  }

  /**
   * Verifica se tem um token válido em cache
   */
  hasValidToken(): boolean {
    const now = Date.now()
    const isExpired = now - this.token.timestamp > this.TOKEN_TTL
    return !!this.token.token && !isExpired
  }

  /**
   * Inicia o preload do token CSRF (chamar na inicialização do app)
   */
  async preload(): Promise<void> {
    if (isNativeApp()) {
      return
    }

    if (this.hasValidToken()) {
      return
    }
    await this.getToken()
  }
}

// Exportar instância singleton
export const csrfManager = new CsrfManager()

/**
 * Hook para usar o CSRF manager em componentes
 */
export function useCsrf() {
  return {
    getToken: (force?: boolean) => csrfManager.getToken(force),
    getTokenSync: () => csrfManager.getTokenSync(),
    invalidate: () => csrfManager.invalidate(),
    hasValidToken: () => csrfManager.hasValidToken(),
    preload: () => csrfManager.preload(),
  }
}
